import _pt from 'prop-types';
import { useMemo, cloneElement, isValidElement } from 'react';
import DOMPurify from 'dompurify';
import convert from 'react-from-dom';
import { css, ClassNames } from '@emotion/react';
import { warning, canUseDOM } from '@commercetools-uikit/utils';
import { designTokens } from '@commercetools-uikit/design-system';
import { jsx } from '@emotion/react/jsx-runtime';

/* eslint-disable @typescript-eslint/no-unused-vars */
const iconSizes = {
  small: 12,
  medium: 16,
  big: 24,
  '10': 12,
  '20': 16,
  '30': 20,
  '40': 24
};
const getSizeDimensions = size => {
  switch (size) {
    case 'scale':
      return {
        width: '100%',
        height: 'auto'
      };
    case 'small':
    case 'medium':
    case 'big':
    case '10':
    case '20':
    case '30':
    case '40':
      return {
        width: `${iconSizes[size]}px`,
        height: `${iconSizes[size]}px`
      };
    default:
      return {
        width: `${iconSizes['40']}px`,
        height: `${iconSizes['40']}px`
      };
  }
};
const getSizeStyle = size => {
  const dimensions = getSizeDimensions(size);
  switch (size) {
    case 'scale':
      return `
        &:not(:root) {
          width: ${dimensions.width};
          height: ${dimensions.height};
        }
      `;
    default:
      return `
        width: ${dimensions.width};
        height: ${dimensions.height};
      `;
  }
};
const getColor = color => {
  if (!color) return 'inherit';
  let iconColor;
  switch (color) {
    case 'solid':
      iconColor = designTokens.colorSolid;
      break;
    case 'neutral60':
      iconColor = designTokens.colorNeutral60;
      break;
    case 'surface':
      iconColor = designTokens.colorSurface;
      break;
    case 'info':
      iconColor = designTokens.colorInfo;
      break;
    case 'primary':
      iconColor = designTokens.colorPrimary;
      break;
    case 'primary40':
      iconColor = designTokens.colorPrimary40;
      break;
    case 'warning':
      iconColor = designTokens.colorWarning;
      break;
    case 'error':
      iconColor = designTokens.colorError;
      break;
    case 'success':
      iconColor = designTokens.colorSuccess;
      break;
  }
  if (!iconColor) {
    process.env.NODE_ENV !== "production" ? warning(color, `ui-kit/Icon: the specified color '${color}' is not supported.`) : void 0;
    return 'inherit';
  }
  return iconColor;
};

// @ts-ignore
const getIconStyles = props => /*#__PURE__*/css("*:not([fill='none']){fill:", getColor(props.color), ";}&,image{", getSizeStyle(props.size), ";}flex-shrink:0;" + (process.env.NODE_ENV === "production" ? "" : ";label:getIconStyles;"), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */");

const InlineSvg = props => {
  const sanitized = useMemo(() => {
    if (!canUseDOM) {
      return props.data;
    }
    return DOMPurify.sanitize(props.data, {
      USE_PROFILES: {
        svg: true
      },
      RETURN_DOM: true,
      FORBID_ATTR: [
      // To avoid injection by using `style="filter:url(\"data:image/svg+xml,<svg`
      'style']
    }).innerHTML;
  }, [props.data]);
  const svgElement = useStringToReactElement(sanitized);
  if (svgElement) {
    return jsx(ClassNames, {
      children: _ref => {
        let css = _ref.css;
        return /*#__PURE__*/cloneElement(svgElement, {
          className: css(getIconStyles(props))
        });
      }
    });
  }
  return null;
};
InlineSvg.propTypes = process.env.NODE_ENV !== "production" ? {
  data: _pt.string.isRequired
} : {};
var InlineSvg$1 = InlineSvg;

// Inspired by https://github.com/gilbarbara/react-inlinesvg
function useStringToReactElement(data) {
  return useMemo(() => {
    if (!canUseDOM) {
      return null;
    }
    try {
      const node = convert(data, {
        nodeOnly: true
      });
      if (!node || !(node instanceof SVGSVGElement)) {
        throw new Error('Could not convert the string to a DOM node');
      }
      const element = convert(node);
      if (!element || ! /*#__PURE__*/isValidElement(element)) {
        throw new Error('Could not convert the DOM node to a React element');
      }
      return element;
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [data]);
}

export { InlineSvg$1 as I };
